/**
 * Match Log Store with Offline-First Architecture
 * Based on Section 4.3: Data Synchronization Flow (Offline-First Strategy)
 *
 * Implements:
 * - Zustand state management
 * - persist middleware with IndexedDB storage (idb-keyval)
 * - isConnected, liveEvents, queuedEvents state
 * - Automatic hydration from IndexedDB on page load
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { get, set, del } from "idb-keyval";
import { compareEventTimestamps } from "../lib/eventTime";

// A GET /matches/:id/events snapshot can be read from the backend before a
// just-acked write it raced against becomes visible to that same read path
// (see setLiveEvents). This bounds how long a locally-confirmed event is
// protected from being dropped by such a stale snapshot -- long enough to
// absorb that race, short enough that a legitimate cross-tab delete (which
// has no other signal reaching this client) still takes effect promptly.
const RECENTLY_CONFIRMED_GRACE_MS = 10000;

// Mirror of RECENTLY_CONFIRMED_GRACE_MS for the opposite race: a GET
// /matches/:id/events snapshot can also be read from the backend before a
// just-undone (locally deleted) event's removal was visible to it. Without
// this, such a stale snapshot resurrects an event the operator just undid
// (see setLiveEvents / recentlyDeletedIds). Same duration, same tradeoff.
const RECENTLY_DELETED_GRACE_MS = 10000;

/**
 * MatchEvent interface based on backend MatchEvent model
 */
export interface MatchEvent {
  _id?: string;
  match_id: string;
  timestamp: string;
  client_id?: string;
  match_clock: string;
  period: number;
  team_id: string;
  player_id?: string;
  staff_id?: string | null;
  location?: [number, number];
  type: string;
  data: Record<string, any>;
  notes?: string | null;
  _confirmed?: boolean;
  _saved_at?: string;
  // Task #37: count of genuine server-side rejections (error acks) this
  // event has received. Only incremented in handleAckResult's error
  // branch — a dropped connection never touches it, since a disconnected
  // send takes the queue path directly. Once it reaches MAX_RETRY_COUNT
  // (useMatchSocket.ts) the event moves to failedEvents instead of back
  // into the retry queue.
  _retryCount?: number;
}

export interface DuplicateHighlight {
  match_clock: string;
  period: number;
  team_id: string;
  timestamp?: string;
  created_at: string;
  existing_event_id?: string | null;
}

export interface DuplicateStats {
  count: number;
  lastMatchClock?: string;
  lastPeriod?: number;
  lastTeamId?: string;
  lastEventType?: string;
  lastSeenAt?: string;
}

/**
 * Match Log Store State
 */
export interface PendingAckEntry {
  event: MatchEvent;
  source: "live" | "queue";
}

interface MatchLogState {
  // Connection status
  isConnected: boolean;
  isHydrated: boolean;

  // Events for this session (rendered in UI)
  liveEvents: MatchEvent[];

  // _id -> confirmedAt (ms). Populated by upsertLiveEvent whenever an ack
  // marks an event _confirmed, cleared once RECENTLY_CONFIRMED_GRACE_MS
  // elapses. setLiveEvents reads this to protect a just-confirmed event
  // from a concurrently in-flight GET /events snapshot that was read
  // before the confirming write became visible to it (see setLiveEvents).
  recentlyConfirmedIds: Record<string, number>;

  // _id -> deletedAt (ms). Mirror of recentlyConfirmedIds for the opposite
  // race: populated by removeLiveEventById/removeLiveEventByClientId
  // whenever a confirmed (server-issued _id) event is removed locally, and
  // consulted by setLiveEvents to SUBTRACT that id from a fetched snapshot
  // for RECENTLY_DELETED_GRACE_MS, so a snapshot read before the delete was
  // visible to the backend cannot resurrect it. If an id is present in both
  // this map and recentlyConfirmedIds, deletion wins -- see setLiveEvents.
  recentlyDeletedIds: Record<string, number>;

  // Events pending server sync (persisted to IndexedDB)
  queuedEvents: MatchEvent[];
  queuedEventsByMatch: Record<string, MatchEvent[]>;

  // Task #37: events that exhausted MAX_RETRY_COUNT genuine server
  // rejections and are no longer retried. Persisted to IndexedDB like
  // queuedEventsByMatch (see partialize below) so an operator who reloads
  // does not lose the record of what failed. Full event objects are kept,
  // not just a count, so the data stays recoverable rather than discarded
  // once counted.
  failedEvents: MatchEvent[];
  failedEventsByMatch: Record<string, MatchEvent[]>;

  // Optimistic events awaiting ack keyed by client request id
  pendingAcks: Record<string, PendingAckEntry>;

  // Current match ID
  currentMatchId: string | null;
  undoStack: string[];

  duplicateHighlight: DuplicateHighlight | null;
  duplicateStats: DuplicateStats;
  operatorClock: string;
  operatorPeriod: number;
  effectiveTime: number; // Accumulated effective time in seconds
  isBallInPlay: boolean;
  lastTimelineRefreshRequest: number;
  lastMatchRefreshRequest: number;

  // Actions
  setConnected: (connected: boolean) => void;
  setIsHydrated: (hydrated: boolean) => void;
  addLiveEvent: (event: MatchEvent) => void;
  setLiveEvents: (events: MatchEvent[], sourceMatchId?: string) => void;
  upsertLiveEvent: (event: MatchEvent) => void;
  removeLiveEventByTimestamp: (timestamp: string) => void;
  removeLiveEventByClientId: (clientId: string) => void;
  removeLiveEventById: (eventId: string) => void;
  updateLiveEventId: (timestamp: string, serverId: string) => void;
  updateEventNotes: (event: MatchEvent, notes: string | null) => void;
  addQueuedEvent: (event: MatchEvent) => void;
  removeQueuedEvent: (event: MatchEvent) => void;
  removeQueuedEventByClientId: (clientId: string) => void;
  addFailedEvent: (event: MatchEvent) => void;
  upsertPendingAck: (clientId: string, entry: PendingAckEntry) => void;
  upsertPendingAcks: (entries: Record<string, PendingAckEntry>) => void;
  resolvePendingAck: (clientId: string) => PendingAckEntry | undefined;
  rejectPendingAck: (clientId: string) => PendingAckEntry | undefined;
  setQueuedEventClientId: (
    matchId: string,
    timestamp: string,
    clientId: string,
  ) => void;
  clearQueuedEvents: () => void;
  clearPendingAcks: () => void;
  setCurrentMatch: (matchId: string | null) => void;
  pushUndoCandidate: (clientId: string) => void;
  removeUndoCandidate: (clientId: string) => void;
  clearUndoStack: () => void;
  setDuplicateHighlight: (highlight: DuplicateHighlight) => void;
  clearDuplicateHighlight: () => void;
  incrementDuplicateStats: (payload: {
    match_clock: string;
    period: number;
    team_id: string;
    type: string;
  }) => void;
  resetDuplicateStats: () => void;
  setOperatorClock: (clock: string) => void;
  setOperatorPeriod: (period: number) => void;
  setEffectiveTime: (time: number) => void;
  setIsBallInPlay: (inPlay: boolean) => void;
  resetOperatorControls: (defaults?: {
    clock?: string;
    period?: number;
  }) => void;
  requestTimelineRefresh: () => void;
  requestMatchRefresh: () => void;
  resetStore: () => void;
}

/**
 * Custom IndexedDB storage using idb-keyval
 * Implements the async StateStorage pattern for zustand/persist
 */
const indexedDBStorage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      const value = await get(name);
      return value || null;
    } catch (error) {
      console.error("Error reading from IndexedDB:", error);
      return null;
    }
  },
  setItem: async (name: string, value: string): Promise<void> => {
    try {
      await set(name, value);
    } catch (error) {
      console.error("Error writing to IndexedDB:", error);
    }
  },
  removeItem: async (name: string): Promise<void> => {
    try {
      await del(name);
    } catch (error) {
      console.error("Error removing from IndexedDB:", error);
    }
  },
};

/**
 * Create the Match Log Store
 * Implements Section 4.3: Client-Side State & Persistence
 */
export const useMatchLogStore = create<MatchLogState>()(
  persist(
    (set, get) => ({
      // Initial state
      isConnected: false,
      isHydrated: false,
      liveEvents: [],
      recentlyConfirmedIds: {},
      recentlyDeletedIds: {},
      queuedEvents: [],
      queuedEventsByMatch: {},
      failedEvents: [],
      failedEventsByMatch: {},
      pendingAcks: {},
      currentMatchId: null,
      undoStack: [],
      duplicateHighlight: null,
      duplicateStats: { count: 0 },
      operatorClock: "00:00.000",
      operatorPeriod: 1,
      effectiveTime: 0,
      isBallInPlay: false,
      lastTimelineRefreshRequest: 0,
      lastMatchRefreshRequest: 0,

      // Set connection status
      setConnected: (connected: boolean) => {
        set({ isConnected: connected });
      },

      setIsHydrated: (hydrated: boolean) => {
        set({ isHydrated: hydrated });
      },

      // Add event to live feed (optimistic UI)
      addLiveEvent: (event: MatchEvent) => {
        set((state) => ({
          liveEvents: [...state.liveEvents, event],
        }));
      },

      setLiveEvents: (events: MatchEvent[], sourceMatchId?: string) => {
        set((state) => {
          const currentMatchId = state.currentMatchId;
          if (
            sourceMatchId &&
            currentMatchId &&
            sourceMatchId !== currentMatchId
          ) {
            return {};
          }

          if (!currentMatchId) {
            return { liveEvents: events };
          }

          // PMA-COCK-218: this used to be
          // `events.filter((event) => event.match_id === currentMatchId)`.
          // That compared two DIFFERENT id domains: `event.match_id` is
          // always the server's CANONICAL business key (every event is
          // persisted through `_resolve_canonical_match_id` before it ever
          // reaches this array), while `currentMatchId` is whatever id
          // shape the CALLING PAGE's route uses -- the canonical key for
          // `/:matchId/logger` (the cockpit links via `item.match_id`), but
          // the Mongo `_id` for `/matches/:matchId/live` (`LiveMatch.tsx`
          // sets it straight from `useParams()`). For every page except
          // the cockpit the two never match, so this filter silently
          // zeroed `events` on every call -- a real, currently-shipping
          // regression: the timeline stayed permanently empty even as
          // `fetchAllMatchEvents` returned real events with a 200.
          // `events` needs no re-scoping here: every caller already
          // obtains it from a single-match-scoped request (
          // `fetchAllMatchEvents` -> `GET
          // /api/v1/logger/matches/{match_id}/events`, which performs the
          // SAME canonical-id resolution server-side before querying), so
          // it cannot contain a different match's events. The protection
          // this filter was reaching for -- guard against a snapshot for a
          // DIFFERENT match landing after the operator navigates away --
          // is `sourceMatchId`'s job above, which compares the SAME id
          // domain on both sides (whatever value the caller itself used to
          // issue the request) instead of two independently-derived ids
          // that can drift out of domain like this one did.
          const rawScopedEvents = events;

          const now = Date.now();

          // Mirror of the recentlyConfirmedIds re-union below, in the
          // opposite direction: a fetched snapshot can just as easily race
          // ahead of a DELETE (undo) as behind a confirming write -- it was
          // read from the backend before that delete became visible to it.
          // Subtract any such id from the snapshot BEFORE it feeds into
          // scopedIds/localOptimistic below. Deletion wins over confirmation
          // on overlap for free: the recentlyConfirmedIds re-union only
          // pulls a protected event from *current* state.liveEvents, which
          // an undone event has already been removed from by the time this
          // runs, so there is nothing left for it to re-add.
          const recentlyDeletedIds: Record<string, number> = {};
          Object.entries(state.recentlyDeletedIds).forEach(
            ([id, deletedAt]) => {
              if (now - deletedAt >= RECENTLY_DELETED_GRACE_MS) return;
              recentlyDeletedIds[id] = deletedAt;
            },
          );
          const scopedEvents = Object.keys(recentlyDeletedIds).length
            ? rawScopedEvents.filter(
                (event) => !event._id || !recentlyDeletedIds[event._id],
              )
            : rawScopedEvents;

          const queued = state.queuedEventsByMatch[currentMatchId] || [];
          const pendingLive = Object.values(state.pendingAcks)
            .filter(
              (entry) =>
                entry.source === "live" &&
                entry.event.match_id === currentMatchId,
            )
            .map((entry) => entry.event);

          // A fetched snapshot can race behind a write it was issued
          // concurrently with: the ack for that write already resolved
          // (so the event is no longer in pendingAcks/queued above) by the
          // time this snapshot -- read from the backend before that write
          // was visible to it -- lands. Re-union any such event, sourced
          // from current liveEvents (not the stale snapshot), for
          // RECENTLY_CONFIRMED_GRACE_MS after its ack. Cross-tab deletes
          // (the scenario this snapshot is otherwise the only signal for)
          // are unaffected: they only ever concern an _id this client did
          // not itself just confirm, so recentlyConfirmedIds has no entry
          // for them.
          const recentlyConfirmedIds: Record<string, number> = {};
          const scopedIds = new Set(
            scopedEvents.map((event) => event._id).filter(Boolean),
          );
          const raceProtected: MatchEvent[] = [];
          Object.entries(state.recentlyConfirmedIds).forEach(
            ([id, confirmedAt]) => {
              if (now - confirmedAt >= RECENTLY_CONFIRMED_GRACE_MS) return;
              recentlyConfirmedIds[id] = confirmedAt;
              if (scopedIds.has(id)) return;
              const current = state.liveEvents.find(
                (event) => event._id === id,
              );
              if (current) raceProtected.push(current);
            },
          );

          const localOptimistic = [...queued, ...pendingLive, ...raceProtected];

          if (localOptimistic.length === 0) {
            return {
              liveEvents: scopedEvents,
              recentlyConfirmedIds,
              recentlyDeletedIds,
            };
          }

          // Merge fetched events with local queued events
          // Avoid duplicates based on client_id or timestamp+type
          const merged = [...scopedEvents];

          localOptimistic.forEach((qEvent) => {
            const exists = merged.some((e) => {
              if (e._id && qEvent._id) {
                return e._id === qEvent._id;
              }
              if (e.client_id && qEvent.client_id) {
                return e.client_id === qEvent.client_id;
              }
              return e.timestamp === qEvent.timestamp && e.type === qEvent.type;
            });

            if (!exists) {
              merged.push(qEvent);
            }
          });

          // Sort by timestamp to maintain timeline order.
          // `compareEventTimestamps`, never raw `new Date(...)`: this merge
          // mixes optimistic events ("...Z") with server-acked ones (naive,
          // no suffix), and a raw parse reads the naive half as local time —
          // in UTC-6 that reorders the whole log by six hours. See
          // `lib/eventTime.ts`.
          merged.sort((a, b) =>
            compareEventTimestamps(a.timestamp, b.timestamp),
          );

          return {
            liveEvents: merged,
            recentlyConfirmedIds,
            recentlyDeletedIds,
          };
        });
      },

      updateEventNotes: (event: MatchEvent, notes: string | null) => {
        const nextNotes = notes || null;
        const matcher = (existing: MatchEvent) => {
          if (event._id && existing._id) return event._id === existing._id;
          if (event.client_id && existing.client_id)
            return event.client_id === existing.client_id;
          return (
            existing.timestamp === event.timestamp &&
            existing.type === event.type &&
            existing.match_clock === event.match_clock
          );
        };

        set((state) => {
          const updateItem = (item: MatchEvent) =>
            matcher(item) ? { ...item, notes: nextNotes } : item;

          const queuedEvents = state.queuedEvents.map(updateItem);
          const liveEvents = state.liveEvents.map(updateItem);
          const queuedEventsByMatch = Object.fromEntries(
            Object.entries(state.queuedEventsByMatch).map(([matchId, list]) => [
              matchId,
              list.map(updateItem),
            ]),
          );

          return { liveEvents, queuedEvents, queuedEventsByMatch };
        });
      },

      upsertLiveEvent: (event: MatchEvent) => {
        set((state) => {
          const idx = state.liveEvents.findIndex((existing) => {
            if (event._id && existing._id) {
              return existing._id === event._id;
            }
            if (event.client_id && existing.client_id) {
              return existing.client_id === event.client_id;
            }
            return (
              existing.timestamp === event.timestamp &&
              existing.type === event.type &&
              existing.match_clock === event.match_clock
            );
          });

          const recentlyConfirmedIds =
            event._confirmed && event._id
              ? { ...state.recentlyConfirmedIds, [event._id]: Date.now() }
              : state.recentlyConfirmedIds;

          if (idx === -1) {
            return {
              liveEvents: [...state.liveEvents, event],
              recentlyConfirmedIds,
            };
          }

          const updated = [...state.liveEvents];
          updated[idx] = { ...updated[idx], ...event };
          return { liveEvents: updated, recentlyConfirmedIds };
        });
      },

      removeLiveEventByTimestamp: (timestamp: string) => {
        set((state) => ({
          liveEvents: state.liveEvents.filter(
            (event) => event.timestamp !== timestamp,
          ),
        }));
      },

      removeLiveEventByClientId: (clientId: string) => {
        if (!clientId) return;
        set((state) => {
          // Record the _id (if the event had already been confirmed) so
          // setLiveEvents can protect against a stale snapshot resurrecting
          // it -- see recentlyDeletedIds.
          const recentlyDeletedIds = { ...state.recentlyDeletedIds };
          const removedAt = Date.now();
          state.liveEvents.forEach((event) => {
            if (event.client_id === clientId && event._id) {
              recentlyDeletedIds[event._id] = removedAt;
            }
          });
          return {
            liveEvents: state.liveEvents.filter(
              (event) => event.client_id !== clientId,
            ),
            recentlyDeletedIds,
          };
        });
      },

      removeLiveEventById: (eventId: string) => {
        if (!eventId) return;
        set((state) => ({
          liveEvents: state.liveEvents.filter((event) => event._id !== eventId),
          recentlyDeletedIds: {
            ...state.recentlyDeletedIds,
            [eventId]: Date.now(),
          },
        }));
      },

      updateLiveEventId: (timestamp: string, serverId: string) => {
        set((state) => ({
          liveEvents: state.liveEvents.map((event) =>
            event.timestamp === timestamp ? { ...event, _id: serverId } : event,
          ),
        }));
      },

      // Add event to queue (offline persistence)
      addQueuedEvent: (event: MatchEvent) => {
        set((state) => {
          const matchQueue = state.queuedEventsByMatch[event.match_id] || [];
          const isCurrentMatch = state.currentMatchId === event.match_id;
          return {
            queuedEvents: isCurrentMatch
              ? [...state.queuedEvents, event]
              : state.queuedEvents,
            queuedEventsByMatch: {
              ...state.queuedEventsByMatch,
              [event.match_id]: [...matchQueue, event],
            },
          };
        });
      },

      // Remove event from queue (after successful sync)
      removeQueuedEvent: (event: MatchEvent) => {
        set((state) => {
          const matchQueue = state.queuedEventsByMatch[event.match_id] || [];
          const filteredMatchQueue = matchQueue.filter(
            (e) => e.timestamp !== event.timestamp || e.type !== event.type,
          );
          const updatedByMatch = {
            ...state.queuedEventsByMatch,
            [event.match_id]: filteredMatchQueue,
          };
          if (!filteredMatchQueue.length) {
            delete updatedByMatch[event.match_id];
          }

          const isCurrentMatch = state.currentMatchId === event.match_id;
          const filtered = isCurrentMatch
            ? state.queuedEvents.filter(
                (e) => e.timestamp !== event.timestamp || e.type !== event.type,
              )
            : state.queuedEvents;

          return {
            queuedEvents: filtered,
            queuedEventsByMatch: updatedByMatch,
          };
        });
      },

      // Task #37: terminal state for an event that exhausted its retry
      // budget. Mirrors addQueuedEvent's shape exactly (current-match
      // array + full by-match map) so it participates in setCurrentMatch's
      // recompute and resetStore's clear the same way queuedEvents does.
      addFailedEvent: (event: MatchEvent) => {
        set((state) => {
          const matchFailed = state.failedEventsByMatch[event.match_id] || [];
          const isCurrentMatch = state.currentMatchId === event.match_id;
          return {
            failedEvents: isCurrentMatch
              ? [...state.failedEvents, event]
              : state.failedEvents,
            failedEventsByMatch: {
              ...state.failedEventsByMatch,
              [event.match_id]: [...matchFailed, event],
            },
          };
        });
      },

      removeQueuedEventByClientId: (clientId: string) => {
        if (!clientId) return;
        set((state) => {
          const updatedByMatch: Record<string, MatchEvent[]> = {};
          Object.entries(state.queuedEventsByMatch).forEach(
            ([matchId, events]) => {
              const filtered = events.filter(
                (event) => event.client_id !== clientId,
              );
              if (filtered.length) {
                updatedByMatch[matchId] = filtered;
              }
            },
          );

          const filteredQueue = state.queuedEvents.filter(
            (event) => event.client_id !== clientId,
          );

          return {
            queuedEventsByMatch: updatedByMatch,
            queuedEvents: filteredQueue,
          };
        });
      },

      // Track optimistic events awaiting ack
      upsertPendingAck: (clientId: string, entry: PendingAckEntry) => {
        set((state) => ({
          pendingAcks: { ...state.pendingAcks, [clientId]: entry },
        }));
      },

      // Batched variant of upsertPendingAck: registers N entries in a single
      // store write instead of N. Callers that register many pending acks in
      // one synchronous pass (e.g. the reconnect queue replay in
      // useMatchSocket.ts's syncQueue) must use this instead of calling
      // upsertPendingAck in a loop, so N queued events never produce N
      // synchronous re-renders of every pendingAcks subscriber in the same
      // tick (React's nested-update guard trips past ~50 chained updates).
      upsertPendingAcks: (entries: Record<string, PendingAckEntry>) => {
        if (!Object.keys(entries).length) return;
        set((state) => ({
          pendingAcks: { ...state.pendingAcks, ...entries },
        }));
      },

      resolvePendingAck: (clientId: string) => {
        const pending = get().pendingAcks[clientId];
        if (pending) {
          set((state) => {
            const { [clientId]: _, ...rest } = state.pendingAcks;
            return { pendingAcks: rest };
          });
        }
        return pending;
      },

      rejectPendingAck: (clientId: string) => {
        const pending = get().pendingAcks[clientId];
        if (pending) {
          set((state) => {
            const { [clientId]: _, ...rest } = state.pendingAcks;
            return { pendingAcks: rest };
          });
        }
        return pending;
      },

      setQueuedEventClientId: (
        matchId: string,
        timestamp: string,
        clientId: string,
      ) => {
        set((state) => {
          const queue = state.queuedEventsByMatch[matchId];
          if (!queue?.length) {
            return {};
          }

          const updatedQueue = queue.map((event) =>
            event.timestamp === timestamp
              ? { ...event, client_id: clientId }
              : event,
          );

          const updates: Partial<MatchLogState> = {
            queuedEventsByMatch: {
              ...state.queuedEventsByMatch,
              [matchId]: updatedQueue,
            },
          };

          if (state.currentMatchId === matchId) {
            updates.queuedEvents = state.queuedEvents.map((event) =>
              event.timestamp === timestamp
                ? { ...event, client_id: clientId }
                : event,
            );
          }

          return updates;
        });
      },

      // Clear all queued events
      clearQueuedEvents: () => {
        set({ queuedEvents: [], queuedEventsByMatch: {} });
      },

      // Clear all pending acks
      clearPendingAcks: () => {
        set({ pendingAcks: {} });
      },

      // Set current match ID
      setCurrentMatch: (matchId: string | null) => {
        const state = get();
        const queuedEvents = matchId
          ? state.queuedEventsByMatch[matchId] || []
          : [];
        const failedEvents = matchId
          ? state.failedEventsByMatch[matchId] || []
          : [];
        const pendingAcks = Object.fromEntries(
          Object.entries(state.pendingAcks).filter(([, entry]) =>
            matchId ? entry.event.match_id === matchId : false,
          ),
        );
        set({
          currentMatchId: matchId,
          liveEvents: [...queuedEvents],
          queuedEvents,
          failedEvents,
          pendingAcks,
          undoStack: [],
        });
      },

      pushUndoCandidate: (clientId: string) => {
        if (!clientId) return;
        set((state) => {
          const MAX_UNDO = 20;
          const deduped = state.undoStack.filter((id) => id !== clientId);
          deduped.push(clientId);
          while (deduped.length > MAX_UNDO) {
            deduped.shift();
          }
          return { undoStack: deduped };
        });
      },

      removeUndoCandidate: (clientId: string) => {
        if (!clientId) return;
        set((state) => ({
          undoStack: state.undoStack.filter((id) => id !== clientId),
        }));
      },

      clearUndoStack: () => {
        set({ undoStack: [] });
      },

      setDuplicateHighlight: (highlight: DuplicateHighlight) => {
        set({ duplicateHighlight: highlight });
      },

      clearDuplicateHighlight: () => {
        set({ duplicateHighlight: null });
      },

      incrementDuplicateStats: ({ match_clock, period, team_id, type }) => {
        set((state) => ({
          duplicateStats: {
            count: state.duplicateStats.count + 1,
            lastMatchClock: match_clock,
            lastPeriod: period,
            lastTeamId: team_id,
            lastEventType: type,
            lastSeenAt: new Date().toISOString(),
          },
        }));
      },

      resetDuplicateStats: () => {
        set({ duplicateStats: { count: 0 } });
      },

      setOperatorClock: (clock: string) => {
        set({ operatorClock: clock });
      },

      setOperatorPeriod: (period: number) => {
        set({ operatorPeriod: period });
      },

      setEffectiveTime: (time: number) => {
        set({ effectiveTime: time });
      },

      setIsBallInPlay: (inPlay: boolean) => {
        set({ isBallInPlay: inPlay });
      },

      resetOperatorControls: (defaults) => {
        set({
          operatorClock: defaults?.clock ?? "00:00.000",
          operatorPeriod: defaults?.period ?? 1,
          effectiveTime: 0,
          isBallInPlay: false,
        });
      },

      requestTimelineRefresh: () => {
        set({ lastTimelineRefreshRequest: Date.now() });
      },

      requestMatchRefresh: () => {
        set({ lastMatchRefreshRequest: Date.now() });
      },

      // Reset store to initial state
      resetStore: () => {
        set({
          isConnected: false,
          isHydrated: true, // Keep hydrated true
          liveEvents: [],
          queuedEvents: [],
          queuedEventsByMatch: {},
          failedEvents: [],
          failedEventsByMatch: {},
          pendingAcks: {},
          currentMatchId: null,
          undoStack: [],
          duplicateHighlight: null,
          duplicateStats: { count: 0 },
          operatorClock: "00:00.000",
          operatorPeriod: 1,
          effectiveTime: 0,
          isBallInPlay: false,
          lastTimelineRefreshRequest: 0,
          lastMatchRefreshRequest: 0,
        });
      },
    }),
    {
      name: "match-log-storage", // IndexedDB key
      storage: createJSONStorage(() => indexedDBStorage),
      // Only persist queued events map and current match
      partialize: (state) => ({
        queuedEventsByMatch: state.queuedEventsByMatch,
        // Task #37: persisted like queuedEventsByMatch, for the same
        // reason — an operator who reloads mid-match must not lose the
        // record of what permanently failed to save.
        failedEventsByMatch: state.failedEventsByMatch,
        currentMatchId: state.currentMatchId,
        operatorClock: state.operatorClock,
        operatorPeriod: state.operatorPeriod,
        effectiveTime: state.effectiveTime,
        isBallInPlay: state.isBallInPlay,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setIsHydrated(true);
      },
    },
  ),
);
